/**
 * KpiCard — single number tile for the dashboard strip.
 *
 * Generic surface: caller passes label + value + optional secondary
 * line (already i18n-ised) + tone for the value colour. We keep this
 * dumb on purpose — formatting/i18n lives in the parent.
 */
import { type ReactNode } from 'react'
import { cn } from '@/lib/cn'

type Tone = 'neutral' | 'pulse' | 'rose' | 'amber' | 'sky'

interface KpiCardProps {
  label: string
  value: ReactNode
  /** Optional dim line under the value (already-formatted). */
  sub?: ReactNode
  tone?: Tone
  loading?: boolean
}

const TONE: Record<Tone, string> = {
  neutral: 'text-text',
  pulse:   'text-pulse',
  rose:    'text-rose',
  amber:   'text-amber',
  sky:     'text-sky',
}

export function KpiCard({
  label, value, sub, tone = 'neutral', loading = false,
}: KpiCardProps) {
  return (
    <div className="bg-ink p-4">
      <div className="text-textFaint text-xs uppercase tracking-wider">
        {label}
      </div>
      <div
        className={cn(
          'font-mono text-2xl mt-2 tabular-nums tracking-tight',
          loading ? 'text-textFaint animate-pulse' : TONE[tone],
        )}
      >
        {loading ? '—' : value}
      </div>
      {sub && (
        <div className="mt-1 text-xs text-textFaint">
          {sub}
        </div>
      )}
    </div>
  )
}
